import "./BusinessSelector.scss";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState, type UIEvent } from "react";
import { IconArrowPoint } from "../../../../../common/icons/IconArrowPoint/IconArrowPoint";
import { LeafErrorMessage } from "../../../../../common/leaf/LeafErrorMessage/LeafErrorMessage";
import { BusinessCard } from "./BusinessCard/BusinessCard";
import type { Business } from "../../../../../services/MyBizShell/mybizShellAPI.types";
import { useAppSelector } from "../../../../../store";
import defaultContent from "../../../../../content/profileSelectorDropdown";

interface BusinessSelectorProps {
  onBack: () => void;
  businesses?: Business[];
  isLoading?: boolean;
  isError?: boolean;
  isKpiLoading?: boolean;
  onSelectBusiness?: (business: Business) => void;
}

export const BusinessSelector = ({
  onBack,
  businesses = [],
  isLoading = false,
  isError = false,
  isKpiLoading = false,
  onSelectBusiness,
}: BusinessSelectorProps) => {
  const { t } = useTranslation(["profileSelectorDropdown"]);
  const { aff: currentAff, abo: currentAbo } = useAppSelector((state) => state.boot.user);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wheelSnapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [listMaxHeight, setListMaxHeight] = useState<number | undefined>(undefined);

  const updateListHeight = useCallback(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    const firstItem = listEl.querySelector<HTMLElement>(".BusinessSelector__list-item");
    if (!firstItem) return;

    const itemHeight = firstItem.getBoundingClientRect().height;
    if (itemHeight === 0) return;

    const parentEl = listEl.closest<HTMLElement>(".ProfileSelectorMenu");
    if (!parentEl) return;

    const parentMaxHeight = parseFloat(getComputedStyle(parentEl).maxHeight);
    if (isNaN(parentMaxHeight)) return;

    const wrapperEl = listEl.closest<HTMLElement>(".BusinessSelector__list-wrapper");
    const headerEl = (wrapperEl?.previousElementSibling ?? listEl.previousElementSibling) as HTMLElement | null;
    const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
    const parentPadding =
      parseFloat(getComputedStyle(parentEl).paddingTop) + parseFloat(getComputedStyle(parentEl).paddingBottom);
    const parentGap = parseFloat(getComputedStyle(parentEl.firstElementChild as Element).getPropertyValue("gap")) || 0;

    const listStyles = getComputedStyle(listEl);
    const listPadding = parseFloat(listStyles.paddingTop) + parseFloat(listStyles.paddingBottom);

    const dividerEl = firstItem.querySelector<HTMLElement>(".BusinessSelector__divider");
    const dividerHeight = dividerEl
      ? dividerEl.getBoundingClientRect().height +
        parseFloat(getComputedStyle(dividerEl).marginTop) +
        parseFloat(getComputedStyle(dividerEl).marginBottom)
      : 0;

    const availableHeight = parentMaxHeight - headerHeight - parentPadding - parentGap;
    const wholeCards = Math.max(1, Math.floor((availableHeight - listPadding) / itemHeight));
    const rawMaxHeight = wholeCards * itemHeight + listPadding - dividerHeight;
    setListMaxHeight(Math.max(0, rawMaxHeight));
  }, []);

  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    let observer: ResizeObserver | undefined;

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => updateListHeight());
      const parentEl = listEl.closest<HTMLElement>(".ProfileSelectorMenu");

      if (parentEl) observer.observe(parentEl);
      observer.observe(listEl);
    }

    const handleWheel = () => {
      listEl.style.scrollSnapType = "none";
      if (wheelSnapTimeoutRef.current) clearTimeout(wheelSnapTimeoutRef.current);
      wheelSnapTimeoutRef.current = setTimeout(() => {
        listEl.style.scrollSnapType = "";
      }, 150);
    };

    listEl.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("resize", updateListHeight);
    updateListHeight();

    return () => {
      observer?.disconnect();
      listEl.removeEventListener("wheel", handleWheel);
      window.removeEventListener("resize", updateListHeight);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (wheelSnapTimeoutRef.current) clearTimeout(wheelSnapTimeoutRef.current);
    };
  }, [updateListHeight, isLoading, isKpiLoading, isError]);

  const updateThumb = useCallback((el: HTMLElement) => {
    const { scrollTop, scrollHeight, clientHeight } = el;
    const thumb = thumbRef.current;
    if (!thumb || scrollHeight <= clientHeight) return;
    const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 60);
    const maxScroll = scrollHeight - clientHeight;
    const top = (scrollTop / maxScroll) * (clientHeight - thumbHeight);
    thumb.style.top = `${top}px`;
    thumb.style.height = `${thumbHeight}px`;
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="BusinessSelector__list" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="BusinessSelector__list-item">
              <div className="BusinessSelector__skeleton" aria-hidden="true" />
            </div>
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="BusinessSelector__error">
          <LeafErrorMessage
            show={true}
            headline={t("businessSelectorErrorHeadline", defaultContent.businessSelectorErrorHeadline)}
            content={t("businessSelectorErrorContent", defaultContent.businessSelectorErrorContent)}
          />
        </div>
      );
    }

    return (
      <div className="BusinessSelector__list-wrapper">
        <div className="BusinessSelector__list" ref={listRef} style={{ maxHeight: listMaxHeight ?? 0 }}>
          {businesses.map((business, index) => {
            const normalizedAbo = business.abo.replace(/^#/, "");
            const isSelected = business.aff === currentAff && normalizedAbo === currentAbo;
            const isDisabled = business.disabledAff || business.isoCountry === "";

            return (
              <div
                key={`${business.aff}-${business.abo}`}
                className="BusinessSelector__list-item"
                data-testid={`business-selector-item-${index}`}
              >
                <BusinessCard
                  business={business}
                  isSelected={isSelected}
                  isKpiLoading={isKpiLoading}
                  onSelect={isDisabled ? undefined : () => onSelectBusiness?.(business)}
                />
                {index < businesses.length - 1 && <div className="BusinessSelector__divider" aria-hidden="true" />}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="BusinessSelector" data-testid="business-selector">
      <div className="BusinessSelector__header">
        <button
          type="button"
          className="BusinessSelector__back"
          onClick={onBack}
          aria-label={t("back", defaultContent.back)}
          data-testid="business-selector-back"
        >
          <IconArrowPoint />
        </button>
        <span className="BusinessSelector__title">{t("selectBusiness", defaultContent.selectBusiness)}</span>
      </div>

      {renderContent()}
    </div>
  );
};
